/**
 * @file /src/lib/utils/document/keywordMatch.ts
 *
 * @description
 * Pure helpers for matching a job's terms against a document (the profile before writing, the resume after):
 *   1. The job's terms and requirements come from its shared analysis (src/lib/utils/fit/jdAnalysis.ts), with no cap.
 *   2. Jev (typesafe/jev-1.14, OpenRouter Decisions API) decides, per term, how well the document supports it.
 *   3. The supported terms and requirements are appended to the writer's user prompt as blocks.
 * This module has no AI or network imports. The route supplies the calls and fails open (no block) on any error.
 */

import type { JdImportance, ProfileRequirementMatch, ResumeAIResponse } from "@/types";

/** Jev context is 32k tokens; keep the document and JD well inside it. */
export const MAX_PROFILE_CHARS = 60000;
export const MAX_JD_CHARS = 15000;

export const JEV_MODEL = "typesafe/jev-1.14";
export const JEV_ENDPOINT = "https://openrouter.ai/api/alpha/decisions";

export const SUPPORT_LEVELS = ["stated", "implied", "bridge", "none"] as const;
export type SupportLevel = (typeof SUPPORT_LEVELS)[number];

export type MatchDocument = "profile" | "resume";

/** Level definitions, worded for the document being checked ("profile" before writing, "resume" for the coverage check). */
export const supportCriteria = (doc: MatchDocument): Record<SupportLevel, string> => ({
  stated: `The ${doc} uses the term or a plain synonym of it (same function, different label).`,
  implied: `The job description names a suite, family or category and the ${doc} names a member of it, so the claim is defensible. Example: JD says Microsoft Office, ${doc} says Excel.`,
  bridge: `The job description names a specific product and the ${doc} names a different product in the same category. Example: JD says Salesforce, ${doc} says HubSpot. Be strict: only when a hiring manager would accept the category as equivalent.`,
  none: `The ${doc} does not support it at any level. Different function, audience or seniority is none, not bridge.`,
});

/**
 * Body for POST /api/alpha/decisions: one Choice question per term, answered independently by Jev.
 * profileText is the text of the document being checked; `document` words the questions for it.
 */
export function buildJevRequest(args: {
  profileText: string;
  jobDescriptionText: string;
  terms: string[];
  document?: MatchDocument;
}) {
  const doc = args.document ?? "profile";
  const questions: Record<string, unknown> = {};
  args.terms.forEach((term, i) => {
    questions[`q${i}`] = {
      type: "choice",
      instructions:
        doc === "resume"
          ? `How well does the resume cover the job description term "${term}"?`
          : `How well does the candidate profile support the job description term "${term}"?`,
      criteria: supportCriteria(doc),
    };
  });
  return {
    model: JEV_MODEL,
    state: {
      [doc === "resume" ? "resume" : "candidate_profile"]: args.profileText.slice(0, MAX_PROFILE_CHARS),
      job_description: args.jobDescriptionText.slice(0, MAX_JD_CHARS),
    } as Record<string, string>,
    questions,
  };
}

/** Flattens the generated resume into plain text for the coverage check. */
export function resumeToText(r: ResumeAIResponse): string {
  const jobs = [...(r.experience ?? []), ...(r.additionalExperience ?? [])].flatMap((e) => [
    `${e.role}, ${e.company}`,
    ...(e.highlights ?? []),
  ]);
  const edu = (r.education ?? []).flatMap((e) => [`${e.degree}, ${e.institution}`, ...(e.accomplishments ?? [])]);
  return [r.headline ?? "", r.summary ?? "", ...jobs, ...edu, ...(r.skills ?? [])].filter(Boolean).join("\n");
}

const normText = (s: string) => s.toLowerCase().replace(/[^a-z0-9%$+#]+/g, " ").trim();

/** Short stable hash (FNV-1a, 32 bit) of the resume text, ignoring case and punctuation. Detects edits since scoring; not for security. */
export function hashResumeText(text: string): string {
  let h = 0x811c9dc5;
  for (const ch of normText(text)) {
    h ^= ch.codePointAt(0) ?? 0;
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

export type KeywordMatch = {
  term: string;
  importance: JdImportance;
  support: SupportLevel;
  /** Jev's concentration statistic. Low when the answer is split between levels, so it is NOT the chance the term is supported. */
  confidence?: number;
  /** Chance the profile supports the term at any level: 1 minus the probability of "none". */
  pSupported?: number;
};

type JevAnswer = { choice?: string; confidence?: number; probabilities?: Record<string, number> };

/** Maps Jev's answers back onto the terms. A missing or unrecognized answer counts as "none". */
export function parseJevResponse(body: unknown, terms: { term: string; importance: JdImportance }[]): KeywordMatch[] {
  const answers = (body as { answers?: Record<string, JevAnswer> })?.answers ?? {};
  return terms.map((t, i) => {
    const a = answers[`q${i}`];
    const support = SUPPORT_LEVELS.find((l) => l === a?.choice) ?? "none";
    const pNone = a?.probabilities?.none;
    return {
      term: t.term,
      importance: t.importance,
      support,
      ...(typeof a?.confidence === "number" ? { confidence: a.confidence } : {}),
      ...(typeof pNone === "number" ? { pSupported: Math.round((1 - pNone) * 100) / 100 } : {}),
    };
  });
}

/**
 * The block appended to the writer's user prompt. Returns "" when nothing is supported.
 * "stated" terms go in as written. "implied" and "bridge" terms are category-level only, per the existing
 * umbrella rules: the writer may use a category label with the tools the profile names, never the JD's product.
 */
export function formatKeywordBlock(matches: Pick<KeywordMatch, "term" | "support">[]): string {
  const direct = matches.filter((m) => m.support === "stated").map((m) => `- ${m.term}`);
  const category = matches
    .filter((m) => m.support === "implied" || m.support === "bridge")
    .map((m) => `- ${m.term}`);
  if (direct.length === 0 && category.length === 0) return "";

  const parts = [
    "\n\n## KEYWORDS TO INCLUDE",
    "The background supports the job description terms below. Work each one into the skills and, where it fits naturally, a bullet or the summary, using the job description's wording. Never claim a tool, product or credential the background does not name.",
  ];
  if (direct.length > 0) parts.push(`Supported directly:\n${direct.join("\n")}`);
  if (category.length > 0) {
    parts.push(
      `Supported at category level only (the background shows related work or names a member of the family, not this term). For a tool, use a category label that lists only the tools the background names, such as "CRM platforms (HubSpot)", and never name the term itself unless the background does. For a skill, domain or responsibility, describe only the related work the background actually shows, using the job description's wording where it is accurate, and do not claim more scope or seniority than the background states:\n${category.join("\n")}`,
    );
  }
  return parts.join("\n");
}

/**
 * The block of job requirements the background shows, appended after the keyword block. Requirements the background
 * does not show are left out, so the writer is never asked to invent evidence. Logistics are never listed.
 */
export function formatRequirementBlock(
  requirements: Pick<ProfileRequirementMatch, "requirement" | "level" | "logistics">[],
): string {
  const usable = requirements.filter((r) => !r.logistics);
  const shown = usable.filter((r) => r.level === "shown").map((r) => `- ${r.requirement}`);
  const partial = usable.filter((r) => r.level === "partial").map((r) => `- ${r.requirement}`);
  if (shown.length === 0 && partial.length === 0) return "";

  const parts = [
    "\n\n## JOB REQUIREMENTS TO SHOW",
    "The background shows the job requirements below. Cover each one in a bullet or the summary, using the specific example the background gives and the job description's wording where it is accurate. Never add scope, scale, results or tools the background does not state.",
  ];
  if (shown.length > 0) parts.push(`Shown with a specific example:\n${shown.join("\n")}`);
  if (partial.length > 0) {
    parts.push(
      `Touched on without a full example (describe only the related work the background shows, and do not stretch it):\n${partial.join("\n")}`,
    );
  }
  return parts.join("\n");
}
